import type {
    AttendanceSessionRef,
    TaskWorkSessionRow,
} from "@/types/attendance";

/**
 * Daily attendance summary numbers — derived from raw `sessions[]` +
 * `taskSessions[]` so an open shift is fully covered up to `closeAtMs`.
 *
 * 镜像自 main-mobile/src/features/attendance/utils/computeAttendanceTotals.ts,
 * 算法与口径完全一致(开放段以 closeAtMs 动态闭合、逐段 floor 到秒、special
 * 分桶 key = taskSubtypeCode ?? subcategoryCode)。mobile 侧若改口径需同步本文件。
 *
 * Invariant:
 *   attendanceSec ≈ workSec + breakSec
 *   workSec       ≈ totalIdleSec + specialTaskSec + nonSpecialTaskSec
 * 子项各自 Math.floor 到秒,所以会有 sub-second 漂移。
 */
export interface AttendanceTotals {
    /** Sum of `clock_out − clock_in` for each pair, open pair closed at `closeAtMs`. Includes break. */
    attendanceSec: number;
    /** Sum of all `break_end − break_start`. Open break closed at `closeAtMs`. */
    breakSec: number;
    /** `attendanceSec − breakSec`. */
    workSec: number;
    /** Sum of `clock_in − previous_clock_out` gaps between shifts. */
    offShiftSec: number;
    /** Sum of on-shift no-task time. */
    totalIdleSec: number;
    /** Time spent in tasks with `taskTypeCode === "special"`. */
    specialTaskSec: number;
    /** Time spent in non-special tasks. */
    nonSpecialTaskSec: number;
    /** Per-type seconds for non-special tasks. Sum = nonSpecialTaskSec. */
    regularByType: Record<string, number>;
    /** Per-subcategory seconds for special tasks. Sum = specialTaskSec. */
    specialBySubcategory: Record<string, number>;
    /** True when the last clock_in has no matching clock_out. */
    hasOpenShift: boolean;
    /** True when the last break_start has no matching break_end. */
    hasOpenBreak: boolean;
}

interface Options {
    /**
     * Moment to close any still-open clock_in / break_start / task.
     *   - today → `Date.now()`
     *   - past   → max timestamp across sessions + task end times
     * Pass `null` to skip closure (open spans get under-counted, legacy behavior).
     */
    closeAtMs: number | null;
}

export function computeAttendanceTotals(
    sessions: AttendanceSessionRef[] | null | undefined,
    taskSessions: TaskWorkSessionRow[] | null | undefined,
    { closeAtMs }: Options,
): AttendanceTotals {
    type Entry =
        | { time: number; kind: "attendance"; ref: AttendanceSessionRef }
        | { time: number; kind: "task"; ref: TaskWorkSessionRow };

    const entries: Entry[] = [];
    for (const s of sessions ?? []) {
        const t = Date.parse(s.actionTime);
        if (!Number.isNaN(t))
            entries.push({ time: t, kind: "attendance", ref: s });
    }
    for (const t of taskSessions ?? []) {
        const ts = Date.parse(t.startedAt);
        if (!Number.isNaN(ts)) entries.push({ time: ts, kind: "task", ref: t });
    }
    entries.sort((a, b) => {
        if (a.time !== b.time) return a.time - b.time;
        if (a.kind === b.kind) return 0;
        return a.kind === "attendance" ? -1 : 1;
    });

    let attendanceSec = 0;
    let breakSec = 0;
    let offShiftSec = 0;
    let idleSec = 0;
    let specialTaskSec = 0;
    let nonSpecialTaskSec = 0;
    const regularByType: Record<string, number> = {};
    const specialBySubcategory: Record<string, number> = {};

    let inShift = false;
    let inBreak = false;
    let shiftStartMs: number | null = null;
    let breakStartMs: number | null = null;
    let lastOnShiftIdleStartMs: number | null = null;
    let lastClockOutMs: number | null = null;

    const addIdle = (endMs: number) => {
        if (
            inShift &&
            !inBreak &&
            lastOnShiftIdleStartMs != null &&
            endMs > lastOnShiftIdleStartMs
        ) {
            idleSec += Math.floor((endMs - lastOnShiftIdleStartMs) / 1000);
        }
    };

    for (const e of entries) {
        if (e.kind === "attendance") {
            const s = e.ref;
            switch (s.sessionType) {
                case "clock_in":
                    if (lastClockOutMs != null && e.time > lastClockOutMs) {
                        offShiftSec += Math.floor(
                            (e.time - lastClockOutMs) / 1000,
                        );
                    }
                    inShift = true;
                    inBreak = false;
                    shiftStartMs = e.time;
                    lastOnShiftIdleStartMs = e.time;
                    lastClockOutMs = null;
                    breakStartMs = null;
                    continue;
                case "clock_out":
                    if (inBreak && breakStartMs != null && e.time > breakStartMs) {
                        breakSec += Math.floor((e.time - breakStartMs) / 1000);
                        breakStartMs = null;
                        inBreak = false;
                    }
                    addIdle(e.time);
                    if (shiftStartMs != null && e.time > shiftStartMs) {
                        attendanceSec += Math.floor(
                            (e.time - shiftStartMs) / 1000,
                        );
                    }
                    inShift = false;
                    shiftStartMs = null;
                    lastOnShiftIdleStartMs = null;
                    lastClockOutMs = e.time;
                    continue;
                case "break_start":
                    addIdle(e.time);
                    inBreak = true;
                    breakStartMs = e.time;
                    lastOnShiftIdleStartMs = null;
                    continue;
                case "break_end":
                    if (inBreak && breakStartMs != null && e.time > breakStartMs) {
                        breakSec += Math.floor((e.time - breakStartMs) / 1000);
                    }
                    inBreak = false;
                    breakStartMs = null;
                    lastOnShiftIdleStartMs = e.time;
                    continue;
            }
            continue;
        }

        // task entry
        const t = e.ref;
        addIdle(e.time);
        const parsedEnd = t.endedAt ? Date.parse(t.endedAt) : null;
        const endedMs =
            parsedEnd != null && !Number.isNaN(parsedEnd) ? parsedEnd : null;
        const effectiveEnd = endedMs ?? closeAtMs;
        if (effectiveEnd != null && effectiveEnd > e.time) {
            const durSec = Math.floor((effectiveEnd - e.time) / 1000);
            if (t.task.taskTypeCode === "special") {
                specialTaskSec += durSec;
                // taskSubtypeCode 是权威字段(mobile 同口径;旧的 subcategoryCode 已废弃,tasks A12)
                const key = t.task.taskSubtypeCode ?? "special.unknown";
                specialBySubcategory[key] =
                    (specialBySubcategory[key] ?? 0) + durSec;
            } else {
                nonSpecialTaskSec += durSec;
                const key = t.task.taskTypeCode || "unknown";
                regularByType[key] = (regularByType[key] ?? 0) + durSec;
            }
        }
        lastOnShiftIdleStartMs = endedMs;
    }

    if (closeAtMs != null) {
        if (inBreak && breakStartMs != null && closeAtMs > breakStartMs) {
            breakSec += Math.floor((closeAtMs - breakStartMs) / 1000);
        }
        if (inShift && shiftStartMs != null && closeAtMs > shiftStartMs) {
            attendanceSec += Math.floor((closeAtMs - shiftStartMs) / 1000);
        }
        if (inShift && !inBreak) addIdle(closeAtMs);
    }

    const workSec = Math.max(0, attendanceSec - breakSec);
    return {
        attendanceSec,
        breakSec,
        workSec,
        offShiftSec,
        totalIdleSec: idleSec,
        specialTaskSec,
        nonSpecialTaskSec,
        regularByType,
        specialBySubcategory,
        hasOpenShift: inShift,
        hasOpenBreak: inBreak,
    };
}

/**
 * 历史日期取最后一个事件时间作为 closeAtMs。无事件返回 null。
 */
export function lastEventMs(
    sessions: AttendanceSessionRef[] | null | undefined,
    taskSessions: TaskWorkSessionRow[] | null | undefined,
): number | null {
    let max = -Infinity;
    for (const s of sessions ?? []) {
        const t = Date.parse(s.actionTime);
        if (!Number.isNaN(t) && t > max) max = t;
    }
    for (const t of taskSessions ?? []) {
        const start = Date.parse(t.startedAt);
        if (!Number.isNaN(start) && start > max) max = start;
        if (t.endedAt) {
            const end = Date.parse(t.endedAt);
            if (!Number.isNaN(end) && end > max) max = end;
        }
    }
    return max === -Infinity ? null : max;
}
